import { FC, useCallback, useState } from 'react';
import UploadIcon from '@icons/UploadIcon/UploadIcon';
import { useDropzone } from 'react-dropzone';
import { ImageFile } from '@features/DemandFormTemplates/model/DemandForm';
import { updateUserImageRequest } from '@api/auth/apiService.ts';
import { UserResponse } from '@api/auth/model/types.ts';
import useToast from '@hooks/useToast/index.tsx';
import {
  DROPZONE_ALLOWED_FILE_TYPES,
  IMAGE_SIZE_LIMIT,
} from '@static/staticData.ts';
import { CircularProgress } from '@mui/material';
import { ImageFileTag } from './ImageFileTag.tsx';
import styles from './UploadForm.module.scss';

/**
 * @param props - The props for the component.
 * @param files - uploaded files which will be displayed.
 * @param onFileUpload - Function which is called after upload new file
 * @param imageUrl - url of uploaded image
 * @param imageAction - upload or remove action for files
 * @param onImageError - called when the image fails to load (an expired link)
 */
interface IUploadFormProps {
  files?: ImageFile[];
  onFileUpload?: (userImageResponse: UserResponse) => void;
  imageUrl?: string;
  imageAction?: (type: 'upload' | 'remove', data?: any) => void;
  onImageError?: () => void;
}

/**
 * @returns A React component that displays an form file upload.
 */
const ImageUploadForm: FC<IUploadFormProps> = (props) => {
  const { files, onFileUpload, imageUrl, onImageError } = props;
  const { showError, showSuccess } = useToast();
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = async (file: any) => {
    if (!file || file.length === 0) return;

    const formData = new FormData();
    formData.append('image', file[0]);

    setIsUploading(true);
    updateUserImageRequest(formData, (userImageResponse) => {
      setIsUploading(false);
      if (userImageResponse) {
        if (onFileUpload) {
          onFileUpload(userImageResponse);
        }
        showSuccess('Success', 'Profile photo updated successfully');
      } else {
        showError(
          'Upload Failed',
          'There was an error uploading your photo. Please try again.'
        );
      }
    });
  };

  const onDrop = useCallback(
    (acceptedFiles: any, rejectedFiles: any) => {
      if (rejectedFiles && rejectedFiles.length > 0) {
        const error = rejectedFiles[0].errors[0];
        if (error.code === 'file-invalid-type') {
          showError('Invalid File Type', 'Please upload a JPG or PNG image.');
        } else if (error.code === 'file-too-large') {
          showError(
            'File Too Large',
            `Image size should be less than ${IMAGE_SIZE_LIMIT / (1024 * 1024)}MB.`
          );
        } else {
          showError('Error', error.message);
        }
        return;
      }

      if (acceptedFiles && acceptedFiles.length > 0) {
        uploadFile(acceptedFiles);
      }
    },
    [showError]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/*': DROPZONE_ALLOWED_FILE_TYPES['image/*'] },
    maxSize: IMAGE_SIZE_LIMIT,
    multiple: false,
  });

  return (
    <div className={styles.wrapper} {...getRootProps()}>
      {imageUrl && (
        <div className={styles.image}>
          <img src={imageUrl} alt="" onError={onImageError} />
          {isUploading && (
            <div className={styles.loadingOverlay}>
              <CircularProgress size={40} />
            </div>
          )}
        </div>
      )}
      <div className={styles.uploadWrapper}>
        <div className={styles.uploadForm}>
          <div className={styles.icon}>
            <div className={styles.inner}>
              <UploadIcon />
            </div>
          </div>
          <input {...getInputProps()} />
          <div className={styles.text}>
            <p>
              <span>Click to upload</span> or drag and drop
              <br />
              JPG, PNG (max. 15Mb)
            </p>
          </div>
        </div>
        <div className={styles.files}>
          {files?.map((file, index) => (
            <ImageFileTag key={index} file={file as any} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImageUploadForm;
